/**
 * useWithdrawDnm — Phase 2 voting hook
 *
 * Calls Election.withdrawDnm() to unlock staked DNM after voting period ends.
 * On success: writes localStorage flag for cross-session Variant B detection (P2-01 r2),
 * then invalidates locked-dnm-status, source-availability, total-voted-this-cycle caches.
 *
 * AC-29 — triggers withdraw
 * AC-30 — success transitions status box Variant A → B
 * AC-31 — contract reverts decoded via registered Election error handler
 * EC-08 — "we are in the voting period" revert → Persian toast
 *
 * See: arandao-docs/pipeline/DAO-FOUND-02/idocs/voting/service-logic.md §2.5
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccount, useChainId, usePublicClient, useSwitchChain } from "wagmi";
import { EXPECTED_CHAIN_ID, FORK_CHAIN_ID } from "@/configs/expected-chain";
import { ELECTION_ABI } from "@/contract/abi/election";
import ContractAddress from "@/contract/addresses";
import { useGuardedWriteContract } from "@/hooks/auth/useGuardedWrite";
import { useCurrentCycle } from "@/hooks/dao/useCurrentCycle";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";
import { setWithdrawnLocalFlag } from "./useLockedDnmStatus";

function isUserRejectionError(err: unknown): boolean {
  return (
    err instanceof Error && err.message.toLowerCase().includes("user rejected")
  );
}

export interface WithdrawDnmMutation {
  mutate: () => void;
  mutateAsync: () => Promise<{ txHash: `0x${string}` }>;
  isPending: boolean;
  isSuccess: boolean;
  reset: () => void;
}

export function useWithdrawDnm(): WithdrawDnmMutation {
  const { address: userAddress } = useAccount();
  const { cycle } = useCurrentCycle();
  const { handleContractError } = useContractErrorHandler();
  const liveChainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useGuardedWriteContract();
  const publicClient = usePublicClient();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess, reset } = useMutation({
    mutationFn: async (): Promise<{ txHash: `0x${string}` }> => {
      // Step 1: Ensure correct chain (D1)
      if (liveChainId !== EXPECTED_CHAIN_ID) {
        await switchChainAsync({ chainId: EXPECTED_CHAIN_ID });
      }

      // Fork-only gas buffer. The Hardhat fork's eth_estimateGas occasionally
      // under-estimates withdrawDnm's per-candidate refund loop (PM hit
      // "out of gas" multiple times before retries landed). On real RPCs the
      // wallet's own estimation is more accurate than anything we can do —
      // don't pre-estimate, don't override, let MetaMask/Rabby handle it.
      let gas: bigint | undefined;
      if (
        liveChainId === FORK_CHAIN_ID &&
        publicClient &&
        userAddress !== undefined
      ) {
        try {
          const estimated = await publicClient.estimateContractGas({
            address: ContractAddress.ELECTION,
            abi: ELECTION_ABI,
            functionName: "withdrawDnm",
            account: userAddress,
          });
          gas = (estimated * 150n) / 100n;
        } catch {
          // Fork estimation occasionally fails too — fall through to wagmi's
          // default. User retry path remains available.
        }
      }

      const txHash = await writeContractAsync({
        address: ContractAddress.ELECTION,
        abi: ELECTION_ABI,
        functionName: "withdrawDnm",
        ...(gas !== undefined ? { gas } : {}),
      });

      if (publicClient) {
        await publicClient.waitForTransactionReceipt({ hash: txHash });
      }

      return { txHash };
    },

    onSuccess: () => {
      // P2-01 r2 — write localStorage flag BEFORE invalidating queries so the
      // next render of useLockedDnmStatus reads it on the same tick (Variant A → B).
      setWithdrawnLocalFlag(userAddress, cycle);

      // P2-02 r2 — 4-segment queryKeys (cycle included) for correct cache invalidation
      queryClient.invalidateQueries({
        queryKey: ["dao", "locked-dnm-status", userAddress, cycle],
      });
      queryClient.invalidateQueries({
        queryKey: ["dao", "source-availability", userAddress, cycle],
      });
      queryClient.invalidateQueries({
        queryKey: ["dao", "total-voted-this-cycle", userAddress, cycle],
      });
    },

    onError: (err) => {
      // AC-31 — decode every error; user rejection is silent
      if (!isUserRejectionError(err)) {
        handleContractError(err);
      }
    },
  });

  return { mutate, mutateAsync, isPending, isSuccess, reset };
}
